import Decimal from 'decimal.js';
import type { ExchangeImportMeta, LedgerEvent } from './types';
import type { FiCsvDiagnosticRow } from './fiDiagnostics';
import {
  cell,
  findColIdx,
  gridFromCsvText,
  headersNormalizedRow0,
  parseDecimalCell,
  parseExchangeTs,
} from './exchangeCsvNormalize';
import { convertUsdToEur } from './eurFx';

export type CoinbaseParseStats = {
  rowsTotal: number;
  mappedAcquisition: number;
  mappedDisposal: number;
  mappedIncome: number;
  mappedInternal: number;
  unmapped: number;
  skippedEmpty: number;
  rawTypeCounts: Record<string, number>;
};

function bump(map: Record<string, number>, key: string): void {
  map[key] = (map[key] ?? 0) + 1;
}

function meta(
  row: number,
  family: string,
  rawType: string,
  ref?: string
): ExchangeImportMeta {
  return {
    detectedExchange: 'coinbase',
    detectedSchemaFamily: family,
    sourceCsvRow: row,
    rawOperationOrType: rawType,
    sourceReference: ref,
  };
}

function pushDiag(
  diagnostics: FiCsvDiagnosticRow[],
  row: number,
  rawType: string,
  asset: string,
  note: string
): void {
  diagnostics.push({
    source: 'coinbase',
    csv_row: row,
    ref: `coinbase_row_${row}`,
    transaction_kind: rawType,
    asset,
    category: 'AMBIGUOUS_REVIEW',
    subreason: 'COINBASE_UNMAPPED_OR_REVIEW',
    note,
  });
}

const STABLE_USD = new Set(['USDT', 'USDC', 'DAI', 'USD', 'PYUSD']);

async function eurFromUsdColumn(
  amt: Decimal | null,
  date: Date,
  signal?: AbortSignal
): Promise<Decimal | null> {
  if (amt == null || !amt.gt(0)) return null;
  const e = await convertUsdToEur(amt.abs(), date);
  return signal?.aborted ? null : e;
}

/** Coinbase Taxes / custom transaction report (coinbase.com). */
export async function parseCoinbaseTaxesTransactionCsv(
  text: string,
  options?: { signal?: AbortSignal; onProgress?: (m: string) => void; yieldEvery?: number }
): Promise<{
  events: LedgerEvent[];
  skipped: string[];
  diagnostics: FiCsvDiagnosticRow[];
  stats: CoinbaseParseStats;
}> {
  const signal = options?.signal;
  const onProgress = options?.onProgress;
  const yieldEvery = options?.yieldEvery ?? 25;
  const grid = gridFromCsvText(text);
  const skipped: string[] = [];
  const events: LedgerEvent[] = [];
  const diagnostics: FiCsvDiagnosticRow[] = [];
  const stats: CoinbaseParseStats = {
    rowsTotal: 0,
    mappedAcquisition: 0,
    mappedDisposal: 0,
    mappedIncome: 0,
    mappedInternal: 0,
    unmapped: 0,
    skippedEmpty: 0,
    rawTypeCounts: {},
  };

  if (grid.length < 2) {
    skipped.push('Coinbase taxes: no data rows');
    return { events, skipped, diagnostics, stats };
  }

  const h = headersNormalizedRow0(grid);
  const ixType = findColIdx(h, [/transaction_type/, /^type$/]);
  const ixAsset = findColIdx(h, [/^asset$/, /^currency$/]);
  const ixQty = findColIdx(h, [/quantity_transacted/, /quantity/]);
  const ixTs =
    findColIdx(h, [/timestamp/, /^time$/, /^date$/]) ??
    findColIdx(h, [/created_at/]);
  const ixSubtotal = findColIdx(h, [/subtotal/]);
  const ixTotal = findColIdx(h, [/total.*inclusive/, /^total$/]);
  const ixFees = findColIdx(h, [/fees/, /spread/]);
  const ixId = findColIdx(h, [/^id$/i, /transaction_id/]);
  const ixNotes = findColIdx(h, [/notes/, /memo/]);

  if (ixType === undefined || ixAsset === undefined || ixQty === undefined) {
    skipped.push('Coinbase taxes: missing Transaction Type / Asset / Quantity columns');
    return { events, skipped, diagnostics, stats };
  }

  const family = 'coinbase_taxes_transaction_report';
  const dataRows = grid.length - 1;
  stats.rowsTotal = dataRows;

  for (let r = 1; r < grid.length; r++) {
    if (signal?.aborted) {
      skipped.push(`Coinbase taxes: stopped at row ${r}`);
      break;
    }
    if (r % yieldEvery === 0) {
      await new Promise((res) => setTimeout(res, 0));
      onProgress?.(`Coinbase taxes: row ${r}/${dataRows}…`);
    }

    const row = grid[r]!;
    const typeRaw = cell(row, ixType);
    const typeNorm = typeRaw.toLowerCase().trim();
    const asset = cell(row, ixAsset).toUpperCase().replace(/\s/g, '');
    const qty = parseDecimalCell(cell(row, ixQty));
    const tss = ixTs !== undefined ? parseExchangeTs(cell(row, ixTs)) : null;
    const id = ixId !== undefined ? cell(row, ixId) : '';
    const notes = ixNotes !== undefined ? cell(row, ixNotes) : '';

    bump(stats.rawTypeCounts, typeRaw || '(empty)');

    if (!typeRaw || qty == null || qty.eq(0) || tss == null || !asset) {
      stats.skippedEmpty++;
      continue;
    }

    const date = new Date(tss);
    const absQ = qty.abs();
    const sub = ixSubtotal !== undefined ? parseDecimalCell(cell(row, ixSubtotal)) : null;
    const total = ixTotal !== undefined ? parseDecimalCell(cell(row, ixTotal)) : null;
    const fees = ixFees !== undefined ? parseDecimalCell(cell(row, ixFees)) : null;

    let eurPrimary: Decimal | null = null;
    const usdHint = sub ?? total ?? fees;
    if (usdHint != null && usdHint.gt(0)) {
      eurPrimary = await eurFromUsdColumn(usdHint.abs(), date, signal);
    }
    if (signal?.aborted) break;

    const exM = meta(r, family, typeRaw, id || undefined);

    const ref = (suffix: string) => `coinbase_tax_${r}_${suffix}`;

    if (typeNorm.includes('reward') || typeNorm.includes('staking') || typeNorm.includes('interest')) {
      events.push({
        ts: tss,
        source: 'coinbase',
        kind: 'income',
        asset,
        amount: absQ,
        eurValue: eurPrimary,
        ref: ref('income'),
        classification: 'INCOME',
        fiTaxCategory: 'TAXABLE_INCOME',
        fiSubreason: 'COINBASE_REWARD',
        explanation: `Coinbase ${typeRaw}`,
        exchangeImport: exM,
        csvMeta: {
          timestampUtc: ixTs !== undefined ? cell(row, ixTs) : '',
          transactionDescription: notes,
          currency: asset,
          amount: qty.toFixed(),
          toCurrency: '',
          toAmount: '',
          nativeCurrency: 'USD',
          nativeAmount: usdHint?.toFixed() ?? '',
          nativeAmountUsd: usdHint?.toFixed() ?? '',
          transactionKind: typeRaw,
          transactionHash: id,
        },
        reviewFlags: eurPrimary ? [] : ['exchange_csv_missing_eur'],
      });
      stats.mappedIncome++;
      continue;
    }

    const isBuy =
      typeNorm === 'buy' ||
      typeNorm.startsWith('buy ') ||
      typeNorm.includes('advanced trade buy');
    const isSell =
      typeNorm === 'sell' ||
      typeNorm.startsWith('sell ') ||
      typeNorm.includes('advanced trade sell');

    if (isBuy && !isSell) {
      events.push({
        ts: tss,
        source: 'coinbase',
        kind: 'acquisition',
        asset,
        amount: absQ,
        eurValue: eurPrimary,
        ref: ref('buy'),
        classification: 'TRANSFER_IN',
        explanation: `Coinbase buy: ${typeRaw}`,
        exchangeImport: exM,
        csvMeta: {
          timestampUtc: ixTs !== undefined ? cell(row, ixTs) : '',
          transactionDescription: notes,
          currency: asset,
          amount: qty.toFixed(),
          toCurrency: 'USD',
          toAmount: sub?.toFixed() ?? total?.toFixed() ?? '',
          nativeCurrency: 'USD',
          nativeAmount: sub?.toFixed() ?? '',
          nativeAmountUsd: sub?.toFixed() ?? '',
          transactionKind: typeRaw,
          transactionHash: id,
        },
        reviewFlags: eurPrimary ? [] : ['exchange_csv_missing_eur'],
      });
      stats.mappedAcquisition++;
      continue;
    }

    if (isSell) {
      events.push({
        ts: tss,
        source: 'coinbase',
        kind: 'disposal',
        asset,
        amount: absQ,
        eurValue: eurPrimary,
        ref: ref('sell'),
        classification: 'TRANSFER_OUT',
        explanation: `Coinbase sell: ${typeRaw}`,
        exchangeImport: exM,
        csvMeta: {
          timestampUtc: ixTs !== undefined ? cell(row, ixTs) : '',
          transactionDescription: notes,
          currency: asset,
          amount: qty.toFixed(),
          toCurrency: 'USD',
          toAmount: sub?.toFixed() ?? total?.toFixed() ?? '',
          nativeCurrency: 'USD',
          nativeAmount: sub?.toFixed() ?? '',
          nativeAmountUsd: sub?.toFixed() ?? '',
          transactionKind: typeRaw,
          transactionHash: id,
        },
        reviewFlags: eurPrimary ? [] : ['exchange_csv_missing_eur'],
      });
      stats.mappedDisposal++;
      continue;
    }

    if (typeNorm.includes('convert')) {
      const signed = qty;
      if (signed.gt(0)) {
        events.push({
          ts: tss,
          source: 'coinbase',
          kind: 'acquisition',
          asset,
          amount: absQ,
          eurValue: eurPrimary,
          ref: ref('convert_in'),
          classification: 'TRANSFER_IN',
          explanation: `Coinbase convert (incoming leg): ${typeRaw}`,
          exchangeImport: exM,
          reviewFlags: ['coinbase_convert', ...(eurPrimary ? [] : ['exchange_csv_missing_eur'])],
        });
        stats.mappedAcquisition++;
      } else {
        events.push({
          ts: tss,
          source: 'coinbase',
          kind: 'disposal',
          asset,
          amount: absQ,
          eurValue: eurPrimary,
          ref: ref('convert_out'),
          classification: 'TRANSFER_OUT',
          explanation: `Coinbase convert (outgoing leg): ${typeRaw}`,
          exchangeImport: exM,
          reviewFlags: ['coinbase_convert', ...(eurPrimary ? [] : ['exchange_csv_missing_eur'])],
        });
        stats.mappedDisposal++;
      }
      continue;
    }

    if (typeNorm === 'receive' || typeNorm.includes('incoming')) {
      events.push({
        ts: tss,
        source: 'coinbase',
        kind: 'acquisition',
        asset,
        amount: absQ,
        eurValue:
          eurPrimary ??
          (STABLE_USD.has(asset) ? await eurFromUsdColumn(absQ, date, signal) : null),
        ref: ref('recv'),
        classification: 'TRANSFER_IN',
        explanation: `Coinbase receive: ${typeRaw}`,
        exchangeImport: exM,
        reviewFlags: [],
      });
      stats.mappedAcquisition++;
      continue;
    }

    if (typeNorm === 'send' || typeNorm.includes('outgoing')) {
      events.push({
        ts: tss,
        source: 'coinbase',
        kind: 'disposal',
        asset,
        amount: absQ,
        eurValue:
          eurPrimary ??
          (STABLE_USD.has(asset) ? await eurFromUsdColumn(absQ, date, signal) : null),
        ref: ref('send'),
        classification: 'TRANSFER_OUT',
        explanation: `Coinbase send: ${typeRaw}`,
        exchangeImport: exM,
        reviewFlags: [],
      });
      stats.mappedDisposal++;
      continue;
    }

    if (
      typeNorm.includes('transfer') &&
      (typeNorm.includes('wallet') || typeNorm.includes('pro') || typeNorm.includes('exchange'))
    ) {
      events.push({
        ts: tss,
        source: 'coinbase',
        kind: 'internal_transfer',
        asset,
        amount: absQ,
        eurValue: null,
        ref: ref('internal'),
        classification: 'INTERNAL_TRANSFER',
        fiTaxCategory: 'NON_TAXABLE_INTERNAL',
        fiSubreason: 'COINBASE_INTERNAL_TRANSFER',
        explanation: `Coinbase internal transfer: ${typeRaw}`,
        exchangeImport: exM,
      });
      stats.mappedInternal++;
      continue;
    }

    stats.unmapped++;
    pushDiag(diagnostics, r, typeRaw, asset, `Unmapped Coinbase transaction type: ${notes}`.trim());
    events.push({
      ts: tss,
      source: 'coinbase',
      kind: 'acquisition',
      asset,
      amount: absQ,
      eurValue: null,
      ref: ref('review'),
      classification: 'UNKNOWN_REVIEW',
      confidence: 'LOW',
      explanation: `Coinbase unmapped: ${typeRaw}`,
      exchangeImport: exM,
      reviewFlags: ['coinbase_unmapped_type'],
    });
  }

  return { events, skipped, diagnostics, stats };
}

/** Coinbase Exchange / Pro **fills** export. */
export async function parseCoinbaseExchangeFillsCsv(
  text: string,
  options?: { signal?: AbortSignal; onProgress?: (m: string) => void; yieldEvery?: number }
): Promise<{
  events: LedgerEvent[];
  skipped: string[];
  diagnostics: FiCsvDiagnosticRow[];
  stats: CoinbaseParseStats;
}> {
  const signal = options?.signal;
  const onProgress = options?.onProgress;
  const yieldEvery = options?.yieldEvery ?? 25;
  const grid = gridFromCsvText(text);
  const skipped: string[] = [];
  const events: LedgerEvent[] = [];
  const diagnostics: FiCsvDiagnosticRow[] = [];
  const stats: CoinbaseParseStats = {
    rowsTotal: 0,
    mappedAcquisition: 0,
    mappedDisposal: 0,
    mappedIncome: 0,
    mappedInternal: 0,
    unmapped: 0,
    skippedEmpty: 0,
    rawTypeCounts: {},
  };

  if (grid.length < 2) {
    skipped.push('Coinbase fills: no data rows');
    return { events, skipped, diagnostics, stats };
  }

  const h = headersNormalizedRow0(grid);
  const ixSide = findColIdx(h, [/^side$/]);
  const ixProduct = findColIdx(h, [/product_id/, /^product$/]);
  const ixSize = findColIdx(h, [/^size$/]);
  const ixPrice = findColIdx(h, [/^price$/]);
  const ixFee = findColIdx(h, [/^fee$/]);
  const ixTs = findColIdx(h, [/created_at/, /trade_time/, /timestamp/]);
  const ixTradeId = findColIdx(h, [/trade_id/]);

  if (ixSide === undefined || ixProduct === undefined || ixSize === undefined || ixPrice === undefined) {
    skipped.push('Coinbase fills: missing side/product/size/price');
    return { events, skipped, diagnostics, stats };
  }

  const family = 'coinbase_exchange_fills';
  const dataRows = grid.length - 1;
  stats.rowsTotal = dataRows;

  for (let r = 1; r < grid.length; r++) {
    if (signal?.aborted) {
      skipped.push(`Coinbase fills: stopped at row ${r}`);
      break;
    }
    if (r % yieldEvery === 0) {
      await new Promise((res) => setTimeout(res, 0));
      onProgress?.(`Coinbase fills: row ${r}/${dataRows}…`);
    }

    const row = grid[r]!;
    const side = cell(row, ixSide).toLowerCase();
    const product = cell(row, ixProduct).toUpperCase();
    const size = parseDecimalCell(cell(row, ixSize));
    const price = parseDecimalCell(cell(row, ixPrice));
    const tss = ixTs !== undefined ? parseExchangeTs(cell(row, ixTs)) : null;
    const tid = ixTradeId !== undefined ? cell(row, ixTradeId) : '';

    bump(stats.rawTypeCounts, side || '?');

    const parts = product.split('-');
    const base = parts[0] ?? product;
    const quote = parts[1] ?? 'USD';

    if (size == null || !size.gt(0) || price == null || tss == null) {
      stats.skippedEmpty++;
      continue;
    }

    const date = new Date(tss);
    const notionalUsd = size.mul(price);
    const eurLeg = STABLE_USD.has(quote) || quote === 'USD'
      ? await eurFromUsdColumn(notionalUsd, date, signal)
      : null;
    if (signal?.aborted) break;

    const exM = meta(r, family, side, tid || undefined);
    const refP = `coinbase_fill_${r}_${base}`;

    if (side === 'buy') {
      events.push({
        ts: tss,
        source: 'coinbase',
        kind: 'acquisition',
        asset: base,
        amount: size.abs(),
        eurValue: eurLeg,
        ref: refP,
        classification: 'TRANSFER_IN',
        explanation: `Coinbase fill BUY ${product}`,
        exchangeImport: exM,
        reviewFlags: eurLeg ? [] : ['exchange_csv_missing_eur'],
      });
      stats.mappedAcquisition++;
    } else if (side === 'sell') {
      events.push({
        ts: tss,
        source: 'coinbase',
        kind: 'disposal',
        asset: base,
        amount: size.abs(),
        eurValue: eurLeg,
        ref: `${refP}_sell`,
        classification: 'TRANSFER_OUT',
        explanation: `Coinbase fill SELL ${product}`,
        exchangeImport: exM,
        reviewFlags: eurLeg ? [] : ['exchange_csv_missing_eur'],
      });
      stats.mappedDisposal++;
    } else {
      stats.unmapped++;
      pushDiag(diagnostics, r, side, base, 'Fill side not buy/sell');
    }

    const fee = ixFee !== undefined ? parseDecimalCell(cell(row, ixFee)) : null;
    if (fee != null && fee.gt(0)) {
      const feeEur = await eurFromUsdColumn(fee, date, signal);
      events.push({
        ts: tss,
        source: 'coinbase',
        kind: 'disposal',
        asset: quote,
        amount: fee.abs(),
        eurValue: feeEur,
        ref: `${refP}_fee`,
        classification: 'TRANSFER_OUT',
        explanation: 'Coinbase fill fee',
        exchangeImport: meta(r, family, `fee_${side}`, tid || undefined),
      });
      stats.mappedDisposal++;
    }
  }

  return { events, skipped, diagnostics, stats };
}

/** Account / balance statement — snapshot-style; diagnostic-only in v1. */
export function parseCoinbaseAccountStatementCsv(
  text: string
): {
  events: LedgerEvent[];
  skipped: string[];
  diagnostics: FiCsvDiagnosticRow[];
  stats: CoinbaseParseStats;
} {
  const grid = gridFromCsvText(text);
  const diagnostics: FiCsvDiagnosticRow[] = [];
  const skipped: string[] = [
    'Coinbase account statement: not mapped to FIFO in v1 — use Taxes report + Fills for activity.',
  ];
  diagnostics.push({
    source: 'coinbase',
    csv_row: 0,
    ref: 'coinbase_account_statement',
    transaction_kind: 'STATEMENT',
    asset: '',
    category: 'AMBIGUOUS_REVIEW',
    subreason: 'COINBASE_ACCOUNT_STATEMENT_V1_SKIP',
    note:
      'Balance/account CSVs are snapshots. Import Raw Transaction History (Taxes) and Exchange Fills instead.',
  });
  return {
    events: [],
    skipped,
    diagnostics,
    stats: {
      rowsTotal: Math.max(0, grid.length - 1),
      mappedAcquisition: 0,
      mappedDisposal: 0,
      mappedIncome: 0,
      mappedInternal: 0,
      unmapped: Math.max(0, grid.length - 1),
      skippedEmpty: 0,
      rawTypeCounts: {},
    },
  };
}
